// Impressive Hero Scroll Animations
// Creates stunning scroll effects on the hero section

class HeroScrollAnimations {
  constructor() {
    this.hero = null;
    this.isMobile = this.checkMobile();
    this.scrollTriggers = []; // Store ScrollTrigger instances
    this.init();
  }

  checkMobile() {
    // Check if device is mobile
    return window.innerWidth <= 749 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  init() {
    this.hero = document.querySelector('.hero-custom');
    if (!this.hero) return;

    // Wait for GSAP and ScrollTrigger
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(() => this.init(), 50);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    // Ensure all text elements are visible on page load
    this.ensureInitialVisibility();
    
    // Initialize all hero animations (now works on mobile and desktop)
    this.initializeAnimations();

    // Setup resize handler to fix disappearing elements
    this.setupResizeHandler();
  }

  initializeAnimations() {
    // Clear any existing ScrollTrigger instances
    this.killAllScrollTriggers();
    
    // Initialize all hero animations
    this.animateBackground();
    this.animateContent();
    this.animateTextSplit();
    this.animateButtons();
    this.animateHeroImage();
  }

  killAllScrollTriggers() {
    // Kill all stored ScrollTrigger instances
    this.scrollTriggers.forEach(trigger => {
      if (trigger && trigger.kill) {
        trigger.kill();
      }
    });
    this.scrollTriggers = [];
  }

  enableMobileMode() {
    // On mobile, ensure all hero elements are visible and reset any hidden states
    const heroContent = this.hero.querySelector('.hero-custom__content');
    if (heroContent) {
      heroContent.style.opacity = '1';
      heroContent.style.transform = 'none';
      heroContent.style.visibility = 'visible';
    }

    // Reset hero text elements
    const heroHeading = this.hero.querySelector('.hero-custom__heading');
    const heroSubheading = this.hero.querySelector('.hero-custom__subheading');
    const heroText = this.hero.querySelector('.hero-custom__text');
    const heroButtons = this.hero.querySelectorAll('.hero-custom__buttons .button');
    
    [heroHeading, heroSubheading, heroText].forEach((el) => {
      if (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.visibility = 'visible';
      }
    });

    heroButtons.forEach((btn) => {
      btn.style.opacity = '1';
      btn.style.transform = 'none';
      btn.style.visibility = 'visible';
    });

    // Reset split text characters if they exist
    const charSpans = heroHeading?.querySelectorAll('.hero-char');
    if (charSpans && charSpans.length > 0) {
      charSpans.forEach((char) => {
        char.style.opacity = '1';
        char.style.transform = 'none';
        char.style.visibility = 'visible';
      });
    }

    // Reset background images/video - reset all images (both desktop and mobile)
    const bgImages = this.hero.querySelectorAll('.hero-custom__img');
    const bgVideo = this.hero.querySelector('.hero-custom__video');
    const bgContainer = this.hero.querySelector('.hero-custom__image');
    
    bgImages.forEach(img => {
      img.style.transform = 'none';
    });
    if (bgVideo) {
      bgVideo.style.transform = 'none';
    }
    if (bgContainer) {
      bgContainer.style.transform = 'none';
    }
  }

  ensureInitialVisibility() {
    // Make sure all hero content is visible on initial page load
    const subheading = this.hero.querySelector('.hero-custom__subheading');
    const heading = this.hero.querySelector('.hero-custom__heading');
    const text = this.hero.querySelector('.hero-custom__text');
    const buttons = this.hero.querySelectorAll('.hero-custom__buttons .button');
    const content = this.hero.querySelector('.hero-custom__content');
    
    [subheading, heading, text, content].forEach((el) => {
      if (el) {
        gsap.set(el, { opacity: 1, y: 0, scale: 1, clearProps: 'all' });
      }
    });
    
    if (buttons.length > 0) {
      gsap.set(buttons, { opacity: 1, y: 0, scale: 1, clearProps: 'all' });
    }

    // Also check for split text characters
    const charSpans = heading?.querySelectorAll('.hero-char');
    if (charSpans && charSpans.length > 0) {
      gsap.set(charSpans, { opacity: 1, y: 0, rotationX: 0, scale: 1 });
    }

    // Add scroll listener to force visibility when at top
    let lastScrollY = window.scrollY;
    window.addEventListener('scroll', () => {
      const currentScrollY = window.scrollY;
      const heroRect = this.hero.getBoundingClientRect();
      const heroTop = heroRect.top;
      
      // If hero is at or near top of viewport, ensure everything is visible
      if (heroTop >= -50 && heroTop <= 100) {
        [subheading, heading, text, content].forEach((el) => {
          if (el) {
            gsap.set(el, { opacity: 1, y: 0, scale: 1 });
          }
        });
        
        if (buttons.length > 0) {
          gsap.set(buttons, { opacity: 1, y: 0, scale: 1 });
        }

        if (charSpans && charSpans.length > 0) {
          gsap.set(charSpans, { opacity: 1, y: 0, rotationX: 0, scale: 1 });
        }
      }
      
      lastScrollY = currentScrollY;
    }, { passive: true });
  }

  animateBackground() {
    // Parallax zoom effect on background image/video - dramatic effect
    const bgImages = this.hero.querySelectorAll('.hero-custom__img');
    const bgVideo = this.hero.querySelector('.hero-custom__video');
    const bgContainer = this.hero.querySelector('.hero-custom__image');
    
    // Use the container for animations so both desktop/mobile images animate together
    const target = bgContainer || bgVideo;
    if (!target) return;

    // Ensure proper transform origin - center to prevent overflow
    gsap.set(target, {
      transformOrigin: 'center center',
    });

    // Ensure hero and containers have overflow hidden via GSAP
    gsap.set(this.hero, {
      overflow: 'hidden',
      maxWidth: '100vw',
    });
    
    if (bgContainer) {
      gsap.set(bgContainer, {
        overflow: 'hidden',
        maxWidth: '100vw',
      });
    }
    
    // Set max-width on all images and video
    if (bgImages.length > 0) {
      bgImages.forEach(img => {
        gsap.set(img, {
          maxWidth: '100vw',
        });
      });
    }
    if (bgVideo) {
      gsap.set(bgVideo, {
        maxWidth: '100vw',
      });
    }

    // Create dramatic zoom and parallax effect as you scroll
    // The overflow: hidden on hero will clip the scaled image, preventing horizontal scroll
    // But the zoom effect will still be visible within the hero bounds
    const tween1 = gsap.to(target, {
      scale: 1.5,
      y: 150,
      ease: 'none',
      scrollTrigger: {
        trigger: this.hero,
        start: 'top top',
        end: 'bottom top',
        scrub: 1.5,
        invalidateOnRefresh: true,
      },
    });
    this.scrollTriggers.push(tween1.scrollTrigger);

    // Fade overlay as you scroll (creates depth)
    const overlay = this.hero.querySelector('.hero-custom__overlay');
    if (overlay) {
      const initialOpacity = parseFloat(getComputedStyle(overlay).opacity) || 1;
      const tween2 = gsap.to(overlay, {
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: this.hero,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
      });
      this.scrollTriggers.push(tween2.scrollTrigger);
    }
  }

  animateContent() {
    const content = this.hero.querySelector('.hero-custom__content');
    if (!content) return;

    // Ensure content starts visible
    gsap.set(content, {
      opacity: 1,
      y: 0,
      scale: 1,
    });

    // Fade out content only when scrolling past hero bottom
    // Use a longer end point to ensure proper reversal
    const tween3 = gsap.to(content, {
      y: -200,
      opacity: 0,
      scale: 0.85,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: this.hero,
        start: 'bottom top',
        end: 'bottom -200%',
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          // Force visibility when hero is in view
          if (self.progress < 0.1) {
            gsap.set(content, { opacity: 1, y: 0, scale: 1 });
          }
        },
      },
    });
    this.scrollTriggers.push(tween3.scrollTrigger);

    // Stagger individual content elements with different speeds
    const heading = this.hero.querySelector('.hero-custom__heading');
    const subheading = this.hero.querySelector('.hero-custom__subheading');
    const text = this.hero.querySelector('.hero-custom__text');
    const buttons = this.hero.querySelector('.hero-custom__buttons');

    const elements = [subheading, heading, text, buttons].filter(Boolean);

    // Ensure elements start visible
    elements.forEach((el) => {
      gsap.set(el, {
        opacity: 1,
        y: 0,
        scale: 1,
      });
    });

    // Animate each element individually with proper reversal
    elements.forEach((el, index) => {
      const tween = gsap.to(el, {
        y: -100 - (index * 30),
        opacity: 0,
        scale: 0.9 - (index * 0.05),
        ease: 'power2.out',
        scrollTrigger: {
          trigger: this.hero,
          start: 'bottom top',
          end: 'bottom -200%',
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            // Force visibility when hero is in view
            if (self.progress < 0.1) {
              gsap.set(el, { opacity: 1, y: 0, scale: 1 });
            }
          },
        },
      });
      this.scrollTriggers.push(tween.scrollTrigger);
    });
  }

  animateTextSplit() {
    // Split text into words for impressive reveal animation
    const heading = this.hero.querySelector('.hero-custom__heading');
    if (!heading) return;

    // Don't re-split if already split
    if (heading.querySelector('.hero-char')) {
      // If already split, ensure visibility and create reversible animation
      const charSpans = heading.querySelectorAll('.hero-char');
      gsap.set(charSpans, {
        opacity: 1,
        y: 0,
        rotationX: 0,
        scale: 1,
      });
      
      // Fade out text that properly reverses
      const tween = gsap.to(charSpans, {
        opacity: 0,
        y: -60,
        rotationX: -90,
        scale: 0.7,
        ease: 'power2.in',
        scrollTrigger: {
          trigger: this.hero,
          start: 'bottom top',
          end: 'bottom -200%',
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            // Force visibility when hero is in view
            if (self.progress < 0.1) {
              gsap.set(charSpans, { opacity: 1, y: 0, rotationX: 0, scale: 1 });
            }
          },
        },
      });
      this.scrollTriggers.push(tween.scrollTrigger);
      return;
    }

    const text = heading.textContent.trim();
    const words = text.split(/\s+/);
    
    // Clear and rebuild with spans for each word
    heading.innerHTML = words
      .map((word) => {
        // Split word into characters for dramatic 3D effect
        const chars = word.split('');
        const charSpans = chars
          .map((char) => {
            if (char === ' ') return '&nbsp;';
            return `<span class="hero-char" style="display: inline-block; opacity: 1; transform: translateY(0px) rotateX(0deg);">${char}</span>`;
          })
          .join('');
        return `<span class="hero-word" style="display: inline-block; margin-right: 0.3em;">${charSpans}</span>`;
      })
      .join(' ');

    const charSpans = heading.querySelectorAll('.hero-char');
    if (charSpans.length === 0) return;
    
    // Set initial state to visible (for page load)
    gsap.set(charSpans, {
      opacity: 1,
      y: 0,
      rotationX: 0,
      scale: 1,
    });

    // Animate characters in with 3D rotation effect on first view
    const tween1 = gsap.fromTo(charSpans, {
      opacity: 0,
      y: 40,
      rotationX: 90,
    }, {
      opacity: 1,
      y: 0,
      rotationX: 0,
      duration: 1.2,
      ease: 'back.out(1.2)',
      stagger: {
        amount: 0.8,
        from: 'start',
      },
      scrollTrigger: {
        trigger: heading,
        start: 'top 90%',
        toggleActions: 'play none none reverse',
      },
    });
    this.scrollTriggers.push(tween1.scrollTrigger);

    // Fade out text that properly reverses
    const tween2 = gsap.to(charSpans, {
      opacity: 0,
      y: -60,
      rotationX: -90,
      scale: 0.7,
      ease: 'power2.in',
      scrollTrigger: {
        trigger: this.hero,
        start: 'bottom top',
        end: 'bottom -200%',
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          // Force visibility when hero is in view
          if (self.progress < 0.1) {
            gsap.set(charSpans, { opacity: 1, y: 0, rotationX: 0, scale: 1 });
          }
        },
      },
    });
    this.scrollTriggers.push(tween2.scrollTrigger);
  }

  animateButtons() {
    const buttons = this.hero.querySelectorAll('.hero-custom__buttons .button');
    if (buttons.length === 0) return;

    // Set initial state to visible (for page load)
    gsap.set(buttons, {
      opacity: 1,
      y: 0,
      scale: 1,
    });

    // Animate in with bounce effect on first view
    const tween1 = gsap.fromTo(buttons, {
      opacity: 0,
      y: 30,
      scale: 0.9,
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      ease: 'back.out(1.2)',
      stagger: 0.1,
      scrollTrigger: {
        trigger: buttons[0],
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
    this.scrollTriggers.push(tween1.scrollTrigger);

    // Fade out buttons that properly reverses when scrolling back up
    const tween2 = gsap.to(buttons, {
      opacity: 0,
      y: -50,
      scale: 0.8,
      ease: 'power2.in',
      scrollTrigger: {
        trigger: this.hero,
        start: 'bottom top',
        end: 'bottom -200%',
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          // Force visibility when hero is in view
          if (self.progress < 0.1) {
            gsap.set(buttons, { opacity: 1, y: 0, scale: 1 });
          }
        },
      },
    });
    this.scrollTriggers.push(tween2.scrollTrigger);
  }

  animateHeroImage() {
    // Animate product image if present
    const heroImage = this.hero.querySelector('.hero-custom__image-block');
    if (!heroImage) return;

    const img = heroImage.querySelector('.hero-custom__block-img');
    if (!img) return;

    // Initial state
    gsap.set(img, {
      opacity: 0,
      scale: 0.8,
      x: 100,
      rotation: -5,
    });

    // Animate in
    const tween1 = gsap.to(img, {
      opacity: 1,
      scale: 1,
      x: 0,
      rotation: 0,
      duration: 1.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: heroImage,
        start: 'top 80%',
        toggleActions: 'play none none none',
      },
    });
    this.scrollTriggers.push(tween1.scrollTrigger);

    // Parallax and scale on scroll
    const tween2 = gsap.to(img, {
      scale: 1.1,
      x: -50,
      rotation: 2,
      ease: 'none',
      scrollTrigger: {
        trigger: this.hero,
        start: 'top top',
        end: 'bottom top',
        scrub: 1.5,
      },
    });
    this.scrollTriggers.push(tween2.scrollTrigger);
  }

  setupResizeHandler() {
    // Handle window resize to fix disappearing elements
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = this.checkMobile();

        // If switching between mobile/desktop, reinitialize
        if (wasMobile !== this.isMobile) {
          if (this.isMobile) {
            // Kill all ScrollTrigger instances
            this.killAllScrollTriggers();
            this.enableMobileMode();
          } else {
            // Reinitialize desktop animations
            window.location.reload();
          }
          return;
        }

        // If still desktop, kill and reinitialize all animations
        if (!this.isMobile && typeof ScrollTrigger !== 'undefined') {
          // Store current scroll position
          const scrollY = window.scrollY;
          const heroRect = this.hero.getBoundingClientRect();
          const heroInView = heroRect.top < window.innerHeight && heroRect.bottom > 0;
          
          // Kill all ScrollTrigger instances
          this.killAllScrollTriggers();
          
          // Reset visibility for hero elements if in view
          if (heroInView) {
            this.forceHeroVisibility();
          }
          
          // Reinitialize animations
          this.initializeAnimations();
          
          // After reinitialization, ensure hero is visible if in view
          if (heroInView) {
            requestAnimationFrame(() => {
              this.forceHeroVisibility();
            });
          }
        } else if (this.isMobile) {
          // Ensure mobile mode is properly applied
          this.enableMobileMode();
        }
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleResize, { passive: true });
  }

  forceHeroVisibility() {
    // Force all hero elements to be visible
    const subheading = this.hero.querySelector('.hero-custom__subheading');
    const heading = this.hero.querySelector('.hero-custom__heading');
    const text = this.hero.querySelector('.hero-custom__text');
    const buttons = this.hero.querySelectorAll('.hero-custom__buttons .button');
    const content = this.hero.querySelector('.hero-custom__content');
    const charSpans = heading?.querySelectorAll('.hero-char');

    [subheading, heading, text, content].forEach((el) => {
      if (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.visibility = 'visible';
      }
    });
    
    if (buttons.length > 0) {
      buttons.forEach(btn => {
        btn.style.opacity = '1';
        btn.style.transform = 'none';
        btn.style.visibility = 'visible';
      });
    }

    if (charSpans && charSpans.length > 0) {
      charSpans.forEach(char => {
        char.style.opacity = '1';
        char.style.transform = 'none';
        char.style.visibility = 'visible';
      });
    }
  }
}

// Initialize
function waitForGSAP(callback, maxAttempts = 50) {
  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    callback();
  } else if (maxAttempts > 0) {
    setTimeout(() => waitForGSAP(callback, maxAttempts - 1), 100);
  }
}

function initHeroScrollAnimations() {
  // Only on home page
  const isHomePage = window.location.pathname === '/' || 
                     window.location.pathname === '/index' || 
                     window.location.pathname === '';
  
  if (document.querySelector('.hero-custom') && isHomePage) {
    new HeroScrollAnimations();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    waitForGSAP(initHeroScrollAnimations);
  });
} else {
  waitForGSAP(initHeroScrollAnimations);
}

